import React from "react";
import { useCombobox } from "downshift";
import PropTypes from "prop-types";
import { useUniqueId } from "../hooks/useUniqueId";
import InputLpg from "../Input";
import AutoSuggestedDropdown from "./AutoSuggestedDropdown";

export const AutoSuggestedInput = (props) => {
  const {
    inputGroupProps,
    dropdownGroupProps,
    technicalGroupProps,
    onBlur,
    suggestedItems,
    ...rest
  } = props;
  const { label, ...inputRest } = inputGroupProps;
  const {
    displaySuggestedItem,
    noResultText,
    resultOverloadedText,
    showSuggestionMinAmount,
    ...dropdownRest
  } = dropdownGroupProps;
  const {
    itemToString,
    onInputValueChange,
    onSelectedItemChange,
    inputValue,
    ...technicalRest
  } = technicalGroupProps;

  const { id } = technicalRest;
  const comboboxId =
    id || `auto-suggested-${useUniqueId("AutoSuggestedInput", props) + 1}`;

  const stateReducer = (state, actionAndChanges) => {
    const {
      type,
      changes,
      props: { items, ...comboBoxRest },
    } = actionAndChanges;
    switch (type) {
      // there is also need to update suggested value onBlur
      case useCombobox.stateChangeTypes.InputBlur: {
        return {
          ...changes,
          ...comboBoxRest,
          selectedItem: state.isOpen
            ? items?.[state.highlightedIndex]
            : undefined,
          isOpen: false,
        };
      }
      case useCombobox.stateChangeTypes.InputChange: {
        return changes.inputValue.length === 0
          ? { ...changes, isOpen: false }
          : changes;
      }
      default:
        return changes;
    }
  };

  const {
    isOpen,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    getInputProps,
    selectedItem,
    getComboboxProps,
  } = useCombobox({
    stateReducer,
    inputValue,
    items: suggestedItems,
    itemToString,
    onSelectedItemChange,
    onInputValueChange,
    defaultHighlightedIndex: 0,
    environment: typeof window !== "undefined" ? window : undefined,
    id: comboboxId,
    ...technicalRest,
  });

  const {
    "aria-labelledby": ariaLabelledBy,
    "aria-controls": ariaControls,
    ref,
    ...inputProps
  } = getInputProps();
  const handleBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }
    inputProps.onBlur(e);
  };

  return (
    <div {...getComboboxProps()}>
      <InputLpg
        {...inputProps}
        labelProps={{ id: ariaLabelledBy }}
        onBlur={handleBlur}
        inputRef={ref}
        {...inputRest}
        {...rest}
      >
        {label}
      </InputLpg>
      <AutoSuggestedDropdown
        getMenuProps={getMenuProps}
        open={isOpen}
        inputValue={inputValue}
        suggestedItems={suggestedItems}
        highlightedIndex={highlightedIndex}
        getItemProps={getItemProps}
        displaySuggestedItem={displaySuggestedItem}
        noResultText={noResultText}
        resultOverloadedText={resultOverloadedText}
        selectedItem={selectedItem}
        showSuggestionMinAmount={showSuggestionMinAmount}
        itemToString={itemToString}
        {...dropdownRest}
      />
    </div>
  );
};

AutoSuggestedInput.propTypes = {
  technicalGroupProps: PropTypes.shape({
    itemToString: PropTypes.func.isRequired,
    inputValue: PropTypes.string.isRequired,
    onInputValueChange: PropTypes.func.isRequired,
    onSelectedItemChange: PropTypes.func.isRequired,
  }).isRequired,
  dropdownGroupProps: PropTypes.shape({
    showSuggestionMinAmount: PropTypes.number.isRequired,
    displaySuggestedItem: PropTypes.func.isRequired,
    noResultText: PropTypes.string.isRequired,
    resultOverloadedText: PropTypes.string.isRequired,
  }).isRequired,
  inputGroupProps: PropTypes.shape({
    label: PropTypes.string.isRequired,
  }).isRequired,
  suggestedItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  onBlur: PropTypes.func,
};

AutoSuggestedInput.defaultProps = {
  onBlur: undefined,
};

AutoSuggestedInput.displayName = "AutoSuggestedInput";

export default AutoSuggestedInput;
